
import { defineComponent } from "vue";
import Footer from "@/components/Footer.vue";
import UsersService from "@/services/users-service";
import { UserDeviceItem } from "@/model/models";
import VueSimpleAlert from "vue3-simple-alert";
import {resolveDeviceContext} from "@/utils/device-context";
import AuthService from "@/services/auth-service";

export default defineComponent({
  name: "ManageDevices",
  components: { Footer },
  data() {
    return {
      devices: [] as UserDeviceItem[],
      loading: false,
      error: "",
      showModal: false,
      showSignOutModal: false,
      selectedDevice: null as UserDeviceItem | null,
      currentDeviceId: "",
    };
  },
  mounted() {
    this.init();
  },
  methods: {
    async init() {
      const device = await resolveDeviceContext();
      this.currentDeviceId = device.device_identifier;
      this.loadDevices();
    },
    doAccount() {
      this.$router.push("/account");
    },
    goEvents () {
      this.$router.push('/events')
    },
    goLinkDevice() {
      this.$router.push("/device");
    },
    loadDevices() {
      this.loading = true;
      this.error = "";

      UsersService.getUserDevices()
          .then((list) => {
            const currentId = (this.currentDeviceId || "").trim();

            // mark + sort
            this.devices = (list || [])
                .map(d => ({ ...d, _isCurrent: currentId && d.deviceId === currentId }))
                .sort((a: any, b: any) => {
                  // current device first
                  if (a._isCurrent && !b._isCurrent) return -1;
                  if (!a._isCurrent && b._isCurrent) return 1;

                  // then by lastSeenAt desc (fallback safe)
                  const at = a.lastSeenAt ? new Date(a.lastSeenAt).getTime() : 0;
                  const bt = b.lastSeenAt ? new Date(b.lastSeenAt).getTime() : 0;
                  return bt - at;
                });

            this.loading = false;
          })
          .catch(() => {
            this.error = "Failed to load devices. Try again later.";
            this.loading = false;
          });
    },
    isCurrentDevice(d: UserDeviceItem) {
      return !!this.currentDeviceId && d.deviceId === this.currentDeviceId;
    },
    openRemoveModal(device: UserDeviceItem) {
      this.selectedDevice = device;
      this.showModal = true;
    },
    closeRemoveModal() {
      this.showModal = false;
      this.selectedDevice = null;
    },
    confirmRemove() {
      if (!this.selectedDevice) return;

      UsersService.signOutDevice(this.selectedDevice.deviceId).then((status) => {
        if (status) {
          this.devices = this.devices.filter(d => d.deviceId !== this.selectedDevice!.deviceId);
        } else {
          this.error = "Failed to remove device. Try again later.";
        }
      }).catch(() => {
        this.error = "Failed to remove device. Try again later.";
      }).finally(() => {
        this.closeRemoveModal();
      });
    },
    formatLastLogin(lastSeenAt: string | null) {
      if (!lastSeenAt) return "—";
      const d = new Date(lastSeenAt);
      if (isNaN(d.getTime())) return lastSeenAt;

      // Date only (uses user locale)
      return d.toLocaleDateString();
    },
    formatEnum(deviceEnum: string) {
      return deviceEnum.replaceAll("_", " ");
    },
    getDeviceIcon(deviceOs: string) {
      const os = (deviceOs || "").toUpperCase();
      if (os === "ANDROID_TV") {
        return require("../assets/images/device-tv.svg");
      }
      if (os === "ANDROID" || os === "IOS") {
        return require("../assets/images/device-mobile.svg");
      }
      return require("../assets/images/device-laptop.svg");
    },
    doLogoutFromAll() {
      resolveDeviceContext().then((device) => {
        const deviceIdentifier = device.device_identifier;
        UsersService.logoutFromAllDevices(deviceIdentifier).then((ok) => {
          if (ok === true) {
            this.showSignOutModal = false;
            VueSimpleAlert.alert("You've been successfully logged out of all devices (except this).", "Logged out");
            this.loadDevices();
          }
        });
      });
    },
    doSignOut() {
      resolveDeviceContext().then((device) => {
        const deviceIdentifier = device.device_identifier;
        UsersService.signOutDevice(deviceIdentifier)
            .catch(() => {
              //console.warn("signOutDevice failed")
            }).finally(() => {
          AuthService.signOut().then(() => {
            this.$router.push("/signin");
          });
        });
      });
    },
  },
});
